<script setup>
import { computed, onMounted, ref } from 'vue';
import { OVERVIEW_METRICS, formatTime } from '../constants';
import { useToggle } from '@vueuse/core';

import MetricCard from './overview/MetricCard.vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useLiveRefresh } from 'dashboard/composables/useLiveRefresh';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import { useI18n } from 'vue-i18n';
const { t } = useI18n();

const uiFlags = useMapGetter('getOverviewUIFlags');
const agentStatus = useMapGetter('agents/getAgentStatus');
const accountConversationMetric = useMapGetter('getAccountConversationMetric');
const agentsPauseStatus = useMapGetter('getAgentsPauseStatus');
const agentsConversationLoad = useMapGetter('getAgentsConversationLoad');
const store = useStore();

const accounti18nKey = 'OVERVIEW_REPORTS.ACCOUNT_CONVERSATIONS';
const teams = useMapGetter('teams/getTeams');

const teamMenuList = computed(() => {
  return [
    { label: t('OVERVIEW_REPORTS.TEAM_CONVERSATIONS.ALL_TEAMS'), value: null },
    ...teams.value.map(team => ({ label: team.name, value: team.id })),
  ];
});

const agentStatusMetrics = computed(() => {
  let metric = {};
  Object.keys(agentStatus.value).forEach(key => {
    const metricName = t(
      `OVERVIEW_REPORTS.AGENT_STATUS.${OVERVIEW_METRICS[key]}`
    );
    metric[metricName] = agentStatus.value[key];
  });
  return metric;
});
const queueWait = computed(
  () => accountConversationMetric.value.queue_wait || {}
);
const conversationMetrics = computed(() => {
  let metric = {};
  Object.keys(accountConversationMetric.value).forEach(key => {
    if (!OVERVIEW_METRICS[key]) return;
    const metricName = t(`${accounti18nKey}.${OVERVIEW_METRICS[key]}`);
    metric[metricName] = accountConversationMetric.value[key];
  });
  return metric;
});

const [showPausedDetails, togglePausedDetails] = useToggle(false);

const elapsedMinutes = startedAt => {
  if (!startedAt) return 0;
  const diff = (Date.now() - new Date(startedAt).getTime()) / 60000;
  return Math.max(0, Math.floor(diff));
};

const formatStartedAt = startedAt => {
  if (!startedAt) return '';
  const d = new Date(startedAt);
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const selectedTeam = ref(null);
const selectedTeamLabel = computed(() => {
  const team =
    teamMenuList.value.find(
      menuItem => menuItem.value === selectedTeam.value
    ) || {};
  return team.label;
});
const fetchData = () => {
  const params = {};
  if (selectedTeam.value) {
    params.team_id = selectedTeam.value;
  }
  store.dispatch('fetchAccountConversationMetric', params);
  store.dispatch('fetchAgentsPauseStatus');
  store.dispatch('fetchAgentsConversationLoad');
};

const { startRefetching } = useLiveRefresh(fetchData);
const [showDropdown, toggleDropdown] = useToggle();

const handleAction = ({ value }) => {
  toggleDropdown(false);
  selectedTeam.value = value;
  fetchData();
};

onMounted(() => {
  fetchData();
  startRefetching();
});
</script>

<template>
  <div class="flex flex-col gap-4">
    <div class="flex flex-col items-stretch md:flex-row gap-4">
      <div
        class="flex flex-1 w-full max-w-full md:w-[65%] md:max-w-[65%] conversation-metric"
      >
        <MetricCard
          :header="t(`${accounti18nKey}.HEADER`)"
          :is-loading="uiFlags.isFetchingAccountConversationMetric"
          :loading-message="t(`${accounti18nKey}.LOADING_MESSAGE`)"
        >
          <template v-if="teams.length" #control>
            <div
              v-on-clickaway="() => toggleDropdown(false)"
              class="relative flex items-center group z-50"
            >
              <Button
                sm
                slate
                faded
                :label="selectedTeamLabel"
                class="capitalize rounded-md group-hover:bg-n-alpha-2"
                @click="toggleDropdown()"
              />
              <DropdownMenu
                v-if="showDropdown"
                :menu-items="teamMenuList"
                class="mt-1 ltr:right-0 rtl:left-0 xl:ltr:right-0 xl:rtl:left-0 top-full"
                label-class="capitalize"
                @action="handleAction($event)"
              />
            </div>
          </template>
          <div
            v-for="(metric, name, index) in conversationMetrics"
            :key="index"
            class="flex-1 min-w-0 pb-2"
          >
            <h3 class="text-base text-n-slate-11">
              {{ name }}
            </h3>
            <p
              class="font-display font-semibold tracking-tight text-n-slate-12 text-3xl mb-0 mt-1"
            >
              {{ metric }}
            </p>
          </div>
        </MetricCard>
      </div>
      <div class="flex flex-1 w-full max-w-full md:w-[35%] md:max-w-[35%]">
        <MetricCard :header="$t('OVERVIEW_REPORTS.AGENT_STATUS.HEADER')">
          <div
            class="grid w-full gap-x-4 grid-cols-[repeat(auto-fit,minmax(min(100%,7rem),1fr))]"
          >
            <div
              v-for="(metric, name, index) in agentStatusMetrics"
              :key="index"
              class="min-w-0 pb-2"
            >
              <h3 class="text-base text-n-slate-11">
                {{ name }}
              </h3>
              <p
                class="font-display font-semibold tracking-tight text-n-slate-12 text-3xl mb-0 mt-1"
              >
                {{ metric }}
              </p>
            </div>
          </div>
        </MetricCard>
      </div>
    </div>

    <div class="flex flex-col items-stretch md:flex-row gap-4">
      <div class="flex-1 w-full max-w-full md:w-1/2">
        <MetricCard :header="$t('OVERVIEW_REPORTS.QUEUE_WAIT.HEADER')">
          <div class="flex-1 min-w-0 pb-2">
            <h3 class="text-base text-n-slate-11">
              {{ $t('OVERVIEW_REPORTS.QUEUE_WAIT.UNASSIGNED') }}
            </h3>
            <p
              class="font-display font-semibold tracking-tight text-n-slate-12 text-3xl mb-0 mt-1"
            >
              {{ queueWait.count || 0 }}
            </p>
          </div>
          <div class="flex-1 min-w-0 pb-2">
            <h3 class="text-base text-n-slate-11">
              {{ $t('OVERVIEW_REPORTS.QUEUE_WAIT.MEDIAN') }}
            </h3>
            <p
              class="font-display font-semibold tracking-tight text-n-slate-12 text-3xl mb-0 mt-1"
            >
              {{ formatTime(queueWait.median_seconds) || '0s' }}
            </p>
          </div>
          <div class="flex-1 min-w-0 pb-2">
            <h3 class="text-base text-n-slate-11">
              {{ $t('OVERVIEW_REPORTS.QUEUE_WAIT.MAX') }}
            </h3>
            <p
              class="font-display font-semibold tracking-tight text-n-slate-12 text-3xl mb-0 mt-1"
            >
              {{ formatTime(queueWait.max_seconds) || '0s' }}
            </p>
          </div>
        </MetricCard>
      </div>
      <div class="flex-1 w-full max-w-full md:w-1/2">
        <MetricCard :header="$t('OVERVIEW_REPORTS.PAUSED_AGENTS.HEADER')">
          <template #control>
            <Button
              sm
              slate
              faded
              :label="
                showPausedDetails
                  ? $t('OVERVIEW_REPORTS.PAUSED_AGENTS.HIDE_DETAILS')
                  : $t('OVERVIEW_REPORTS.PAUSED_AGENTS.SHOW_DETAILS')
              "
              class="rounded-md"
              @click="togglePausedDetails()"
            />
          </template>
          <div class="flex flex-col w-full">
            <p
              class="font-display font-semibold tracking-tight text-n-slate-12 text-3xl mb-2 mt-1"
            >
              {{ agentsPauseStatus.length }}
            </p>
            <div v-if="showPausedDetails" class="flex flex-col gap-2 w-full">
              <p
                v-if="!agentsPauseStatus.length"
                class="text-sm text-n-slate-11 mb-0"
              >
                {{ $t('OVERVIEW_REPORTS.PAUSED_AGENTS.EMPTY') }}
              </p>
              <div
                v-for="session in agentsPauseStatus"
                :key="session.user_id"
                class="flex flex-row justify-between items-center py-2 px-3 rounded-md bg-n-alpha-1"
              >
                <div class="flex flex-col">
                  <span class="text-sm text-n-slate-12 font-medium">
                    {{ session.user_name }}
                  </span>
                  <span class="text-xs text-n-slate-11">
                    {{ session.pause_type_name }}
                  </span>
                </div>
                <div class="flex flex-col items-end">
                  <span class="text-xs text-n-slate-11">
                    {{
                      $t('OVERVIEW_REPORTS.PAUSED_AGENTS.STARTED_AT', {
                        time: formatStartedAt(session.started_at),
                      })
                    }}
                  </span>
                  <span class="text-xs text-n-slate-11">
                    {{
                      $t('OVERVIEW_REPORTS.PAUSED_AGENTS.ELAPSED', {
                        minutes: elapsedMinutes(session.started_at),
                      })
                    }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </MetricCard>
      </div>
    </div>

    <div class="w-full">
      <MetricCard :header="$t('OVERVIEW_REPORTS.CONVERSATION_LOAD.HEADER')">
        <div class="flex flex-col w-full">
          <p
            v-if="!agentsConversationLoad.length"
            class="text-sm text-n-slate-11 mb-0"
          >
            {{ $t('OVERVIEW_REPORTS.CONVERSATION_LOAD.EMPTY') }}
          </p>
          <table v-else class="w-full text-sm">
            <thead>
              <tr class="text-left text-n-slate-11 border-b border-n-weak">
                <th class="py-2 font-medium">
                  {{ $t('OVERVIEW_REPORTS.CONVERSATION_LOAD.AGENT') }}
                </th>
                <th class="py-2 font-medium text-right">
                  {{ $t('OVERVIEW_REPORTS.CONVERSATION_LOAD.OPEN') }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in agentsConversationLoad"
                :key="row.user_id"
                class="border-b border-n-weak last:border-b-0"
              >
                <td class="py-2 text-n-slate-12">
                  {{ row.user_name }}
                </td>
                <td class="py-2 text-right text-n-slate-12">
                  {{ row.open_conversations_count }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </MetricCard>
    </div>
  </div>
</template>
